import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  AlTableColumn,
  AlTableRowAction,
} from '../../components/features/table/table.component';
import { CoursePrerequisitesDialogComponent } from '../../components/dialogs/course-prerequisites-dialog/course-prerequisites-dialog.component';
import { CourseResponsiblesDialogComponent } from '../../components/dialogs/course-responsibles-dialog/course-responsibles-dialog.component';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../services/auth.service';
import { CurriculumService } from '../../services/curriculum.service';
import { UserService } from '../../services/user.service';

import {
  IliasConnection,
  IliasCourseRow,
  IliasOverviewService,
  IliasTeacherRow,
  IliasUserRow,
} from '../../services/ilias-overview.service';

type IliasTab = 'courses' | 'users' | 'teachers';

const PAGE_SIZE = 25;

/**
 * ILIAS-Übersicht — das Gegenstück zur Moodle-Übersicht.
 *
 * Zeigt, **was es in ILIAS gibt**: Kurse und Gruppen, Nutzer, Lehrende. Wer
 * welchen Agent wo bekommt, ist eine andere Frage und steht unter
 * Platzierungen. Die Spalte „Agenten" hier ist nur ein Zähler, kein
 * Bedienelement — sonst verschwämme die Trennung.
 *
 * Die Daten stammen aus dem SOAP-Sync (`lms_external_objects`,
 * `lms_external_users`, `lms_external_memberships`). Eingerichtet wird der
 * Abgleich **nicht hier**, sondern im Verbindungsdialog unter Integrationen —
 * dieselbe Aufteilung wie bei Moodle, wo die Kachel den Zugang regelt und
 * `/moodle` die Daten zeigt.
 */
@Component({
  selector: 'app-ilias-dashboard',
  templateUrl: './ilias-dashboard.component.html',
  styleUrl: './ilias-dashboard.component.scss',
})
export class IliasDashboardComponent implements OnInit, OnDestroy {
  connections: IliasConnection[] = [];
  selectedConnectionId: string | null = null;

  activeTab: IliasTab = 'courses';
  loading = false;
  syncing = false;
  savingSettings = false;

  courses: IliasCourseRow[] = [];
  users: IliasUserRow[] = [];
  teachers: IliasTeacherRow[] = [];

  total: Record<IliasTab, number> = { courses: 0, users: 0, teachers: 0 };
  page: Record<IliasTab, number> = { courses: 0, users: 0, teachers: 0 };
  search: Record<IliasTab, string> = { courses: '', users: '', teachers: '' };

  readonly pageSize = PAGE_SIZE;

  private destroy$ = new Subject<void>();
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private ilias: IliasOverviewService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private dialog: NbDialogService,
    private curriculum: CurriculumService,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  /** Spalten der Kursliste. Dieselben Werte wie zuvor in der handgebauten
   *  Tabelle; `kind` gesetzt, damit Zahlen fluchten und der Pfad umbrechen
   *  darf, statt die Tabelle breitzudruecken. */
  get courseColumns(): AlTableColumn[] {
    const key = this.t.currentLang;
    if (key === this.courseColumnsKey && this.courseColumnsCache.length) {
      return this.courseColumnsCache;
    }
    this.courseColumnsKey = key;
    this.courseColumnsCache = [
      { key: 'title', title: this.t.instant('pages.ilias.col.title'), width: '26%', kind: 'name', sortable: true, map: (v: any) => v || '—' },
      { key: 'object_type', title: this.t.instant('pages.ilias.col.type'), map: (v: any) => this.objectTypeLabel(v) },
      { key: 'path', title: this.t.instant('pages.ilias.col.path'), kind: 'text', map: (v: any) => v || '—' },
      { key: 'ref_id', title: this.t.instant('pages.ilias.col.refId'), kind: 'number' },
      { key: 'member_count', title: this.t.instant('pages.ilias.col.members'), kind: 'number' },
      { key: 'teacher_count', title: this.t.instant('pages.ilias.col.teachers'), kind: 'number' },
      { key: 'placement_count', title: this.t.instant('pages.ilias.col.placements'), kind: 'number', map: (v: any) => v || '—' },
      { key: 'result_count', title: this.t.instant('pages.ilias.col.results'), kind: 'number', map: (v: any) => v || '—' },
    ];
    return this.courseColumnsCache;
  }

  /** Zeilenmenue der Kursliste.
   *
   *  Alles hier adressiert `courses.id` — unseren eigenen Kurs hinter dem
   *  ILIAS-Objekt. Den gibt es erst, wenn an dem Objekt ein Agent haengt
   *  oder Inhalte geholt wurden; vorher sind die Eintraege gesperrt und
   *  sagen auch warum, statt beim Klick ins Leere zu laufen.
   *
   *  Kursstruktur und Schwierigkeits-Report fehlen bewusst: ihre Endpunkte
   *  loesen ueber `moodle_course_uuid` auf und koennen einen ILIAS-Kurs
   *  derzeit nicht finden. */
  readonly courseRowActions: AlTableRowAction[] = [
    {
      key: 'responsibles',
      title: this.t.instant('pages.ilias.actions.responsibles'),
      icon: 'people-outline',
      disabled: (row: IliasCourseRow) => !row.course_id,
      tooltip: (row: IliasCourseRow) =>
        row.course_id ? '' : this.t.instant('pages.ilias.actions.needsCourse'),
    },
    {
      key: 'prerequisites',
      title: this.t.instant('pages.ilias.actions.prerequisites'),
      icon: 'link-2-outline',
      disabled: (row: IliasCourseRow) => !row.course_id,
      tooltip: (row: IliasCourseRow) =>
        row.course_id ? '' : this.t.instant('pages.ilias.actions.needsCourse'),
    },
    {
      key: 'assignMe',
      title: this.t.instant('pages.ilias.actions.assignMe'),
      icon: 'person-done-outline',
      isMultiAction: true,
      multiOnly: true,
    },
    {
      key: 'assignSomeone',
      title: this.t.instant('pages.ilias.actions.assignSomeone'),
      icon: 'people-outline',
      isMultiAction: true,
      multiOnly: true,
    },
  ];

  private courseColumnsKey = '';
  private courseColumnsCache: AlTableColumn[] = [];

  onCourseTableEvent(e: any): void {
    const rows: IliasCourseRow[] = e?.data?.rows || (e?.data?.row ? [e.data.row] : []);
    const key = e?.data?.action?.key;
    if (!key) return;
    if (key === 'assignMe') {
      this.assignResponsible(rows, null);
      return;
    }
    if (key === 'assignSomeone') {
      this.pickAndAssignResponsible(rows);
      return;
    }
    const row = rows[0];
    if (!row?.course_id) return;
    if (key === 'responsibles') {
      this.dialog
        .open(CourseResponsiblesDialogComponent, {
          context: { courseId: row.course_id, courseName: row.title || '' },
          hasBackdrop: true,
        })
        .onClose.subscribe(() => this.loadActiveTab());
    }
    if (key === 'prerequisites') {
      // Die Auswahl listet unsere eigenen Kurse — eine Verknuepfung kann nur
      // auf einen zeigen, zu dem wir Inhalte halten. Objekte ohne Kurs
      // bleiben deshalb draussen.
      const options = this.courses
        .filter((r) => !!r.course_id && r.course_id !== row.course_id)
        .map((r) => ({ id: r.course_id as string, name: r.title || '', hasResponsible: true }));
      this.curriculum.mayEditCourse(row.course_id).subscribe({
        next: (may) => this.showPrerequisites(row, options, may),
        error: () => this.showPrerequisites(row, options, false),
      });
    }
  }

  private showPrerequisites(
    row: IliasCourseRow,
    courses: { id: string; name: string; hasResponsible: boolean }[],
    canRequest: boolean,
  ): void {
    this.dialog.open(CoursePrerequisitesDialogComponent, {
      context: {
        courseId: row.course_id as string,
        courseName: row.title || '',
        courses,
        canRequest,
      },
      hasBackdrop: true,
    });
  }

  /** Erst die Person waehlen, dann zuordnen — sonst verspraeche der
   *  Menueeintrag eine Auswahl und traege stillschweigend die eigene Kennung
   *  ein. Gleiche Fuehrung wie im Moodle-Dashboard. */
  private pickAndAssignResponsible(rows: IliasCourseRow[]): void {
    this.userService.listUsers().subscribe((users: any[]) => {
      const options = (users || []).map((u) => ({
        value: u.id,
        label: (u.name || '').trim() ? `${u.name} · ${u.email}` : u.email,
      }));
      if (!options.length) {
        this.toastr.warning(
          this.t.instant('pages.ilias.actions.noUsers'),
          this.t.instant('pages.ilias.actions.responsibles'),
        );
        return;
      }
      this.dialog
        .open(ConfirmDialogComponent, {
          context: {
            title: this.t.instant('pages.ilias.actions.assignSomeone'),
            message: this.t.instant('pages.ilias.actions.assignFor', { count: rows.length }),
            options,
            selectedOption: options[0].value,
            confirmLabel: this.t.instant('pages.ilias.actions.assignConfirm'),
          },
          hasBackdrop: true,
        })
        .onClose.subscribe((userId: any) => {
          if (userId) this.assignResponsible(rows, String(userId));
        });
    });
  }

  /** Eine Person fuer mehrere Kurse auf einmal benennen — ohne sie kann
   *  niemand einer Verknuepfung zustimmen. Objekte ohne eigenen Kurs werden
   *  uebersprungen und am Ende genannt, statt die Aktion scheitern zu
   *  lassen. */
  private assignResponsible(rows: IliasCourseRow[], userId: string | null): void {
    const target = userId || this.authService.accessTokenPayload$.value?.user_id;
    if (!target) {
      this.toastr.danger(
        this.t.instant('pages.ilias.actions.noUser'),
        this.t.instant('pages.ilias.actions.responsibles'),
      );
      return;
    }
    const usable = rows.filter((r) => !!r.course_id);
    const skipped = rows.length - usable.length;
    if (!usable.length) {
      this.toastr.warning(
        this.t.instant('pages.ilias.actions.needsCourse'),
        this.t.instant('pages.ilias.actions.responsibles'),
      );
      return;
    }
    forkJoin(
      usable.map((r) =>
        this.curriculum.grantResponsible(r.course_id as string, target).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
    ).subscribe((results) => {
      const ok = results.filter(Boolean).length;
      if (ok) {
        this.toastr.success(
          this.t.instant('pages.ilias.actions.assigned', { count: ok }) +
            (skipped ? ' · ' + this.t.instant('pages.ilias.actions.skipped', { count: skipped }) : ''),
          this.t.instant('pages.ilias.actions.responsibles'),
        );
        this.loadActiveTab();
      } else {
        this.toastr.danger(
          this.t.instant('pages.ilias.actions.assignFailed'),
          this.t.instant('pages.ilias.actions.responsibles'),
        );
      }
    });
  }

  ngOnInit(): void {
    this.ilias
      .listConnections()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.connections = rows;
          this.selectedConnectionId = rows.length ? rows[0].id : null;
          this.loadActiveTab();
        },
        error: () => {
          this.connections = [];
        },
      });
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------
  // Verbindung
  // ---------------------------------------------------------------------

  get connection(): IliasConnection | null {
    return this.connections.find((c) => c.id === this.selectedConnectionId) || null;
  }

  /** Ohne SOAP-Zugang gibt es nichts zu holen — dann wäre „Jetzt abgleichen"
   *  eine Sackgasse. Eingerichtet wird der Zugang im Verbindungsdialog unter
   *  Integrationen, nicht hier. */
  get soapConfigured(): boolean {
    return !!this.connection?.soap_configured;
  }

  /** „Jetzt abgleichen" im Seitenkopf — wie der Auffrischen-Knopf auf der
   *  Moodle-Übersicht. Eingerichtet wird der Abgleich im Verbindungsdialog;
   *  hier lässt er sich nur anstossen. */
  runSync(): void {
    const connection = this.connection;
    if (!connection || this.syncing) return;
    this.syncing = true;
    this.ilias.runSync(connection.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.syncing = false;
        this.toastr.success(
          this.t.instant('pages.ilias.syncDone', {
            courses: result.tree?.courses ?? 0,
            users: result.members?.users ?? 0,
          }), '',
        );
        this.loadActiveTab();
      },
      error: (err) => {
        this.syncing = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('pages.ilias.syncFailed'), '',
        );
      },
    });
  }

  onConnectionChange(id: string): void {
    this.selectedConnectionId = id;
    this.page = { courses: 0, users: 0, teachers: 0 };
    this.loadActiveTab();
  }

  // ---------------------------------------------------------------------
  // Tabs und Listen
  // ---------------------------------------------------------------------

  onTabChange(tabId: string): void {
    this.activeTab = (tabId as IliasTab) || 'courses';
    this.loadActiveTab();
  }

  onSearchChange(value: string): void {
    this.search[this.activeTab] = value;
    this.page[this.activeTab] = 0;
    // Entprellt, weil jede Eingabe sonst eine Abfrage über die ganze
    // Nutzerliste auslöst.
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.loadActiveTab(), 300);
  }

  get currentTotal(): number {
    return this.total[this.activeTab];
  }

  get currentPage(): number {
    return this.page[this.activeTab];
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.currentTotal / PAGE_SIZE));
  }

  goToPage(next: number): void {
    const clamped = Math.max(0, Math.min(next, this.pageCount - 1));
    if (clamped === this.page[this.activeTab]) return;
    this.page[this.activeTab] = clamped;
    this.loadActiveTab();
  }

  loadActiveTab(): void {
    if (!this.selectedConnectionId) return;
    const tab = this.activeTab;
    const opts = {
      registrationId: this.selectedConnectionId,
      search: this.search[tab],
      limit: PAGE_SIZE,
      offset: this.page[tab] * PAGE_SIZE,
    };
    this.loading = true;

    const done = () => {
      this.loading = false;
    };
    const fail = () => {
      this.loading = false;
      this.total[tab] = 0;
      if (tab === 'courses') this.courses = [];
      if (tab === 'users') this.users = [];
      if (tab === 'teachers') this.teachers = [];
    };

    if (tab === 'courses') {
      this.ilias.listCourses(opts).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.courses = res.rows;
          this.total.courses = res.total;
          done();
        },
        error: fail,
      });
    } else if (tab === 'users') {
      this.ilias.listUsers(opts).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.users = res.rows;
          this.total.users = res.total;
          done();
        },
        error: fail,
      });
    } else {
      this.ilias.listTeachers(opts).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.teachers = res.rows;
          this.total.teachers = res.total;
          done();
        },
        error: fail,
      });
    }
  }

  // ---------------------------------------------------------------------
  // Darstellung
  // ---------------------------------------------------------------------

  personName(row: { first_name: string | null; last_name: string | null; login: string | null }): string {
    const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
    return name || row.login || '—';
  }

  objectTypeLabel(type: string): string {
    return this.t.instant(
      type === 'grp' ? 'pages.ilias.objectType.grp' : 'pages.ilias.objectType.crs',
    );
  }

  roleLabel(role: string): string {
    return this.t.instant(`pages.ilias.role.${role}`, {});
  }
}
